import React, { useState } from "react";
import { useRouter } from "next/router";
import {
  IconButton,
  Menu,
  Box,
  MenuItem,
  ListItemIcon,
  Stack,
  Typography,
  ListItemText,
} from "@mui/material";
import { AiOutlineMenu } from "react-icons/ai";

import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { routes } from "@/global/routes";
import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import {
  authOriginUIDs,
  authUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { useAuthCtx } from "@/global/context/Auth";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import MarkUnreadChatAltOutlinedIcon from "@mui/icons-material/MarkUnreadChatAltOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { CreditCardRounded } from "@mui/icons-material";
import { HamburgerMenuUser } from "@/shared/common/HamburgerMenuUser";
import { useTranslation } from "react-i18next";

interface OptionsMenuProps {
  renderDynamicItems?: () => React.ReactNode;
}

const OptionsMenu = (props: OptionsMenuProps) => {
  const router = useRouter();
  const { md: mdDown } = useCssMediaQuery("down");
  const { t } = useTranslation();
  const {
    teamSchema,
    giftCardConfigs,
    languages,
    availableCreditEnabled,
    defaultRoute,
    intercomConfigs,
    platform,
    shouldUseInternalLogin,
    oktaClientId,
    oktaIssuer,
    authConfigs,
    manageCard,
    uiConfigs,
  } = useTeamConfig();
  const [anchorEl, setAnchorEl] = useState(null);
  const { logoutTicketMaster } = useAuthCtx();
  const user = useAppStore(selectUser);
  const userIsLegible = user?.userId;
  const isGiftCardStandalonePage = router.route === routes.gift;
  const { handleConnectTm } = useTicketmaster();
  const { handleLoginOkta, handleSignOut } = useTicketsdotcom();
  const { handleInternalRedirect, handleInternalLogOut } = useInternalAuth();

  const optionsMenuLoginButtonLanguage = languages?.optionsMenuLoginButtonLanguage || "Login"; // prettier-ignore
  const manageCardOption = manageCard || false;
  const purchaseHistoryEnabled = uiConfigs?.hamburgerMenu?.purchaseHistoryEnabled ?? true; // prettier-ignore
  const logoutButtonEnabled = uiConfigs?.hamburgerMenu?.logoutButtonEnabled ?? true; // prettier-ignore

  const availableCredit = useAppStore((state) => {
    return state.data.creditReport.availableCredit;
  });

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGoToRoute = (route: string) => {
    router.push({
      pathname: route,
      query: {
        teamSchema,
      },
    });
  };

  const handleLogin = async () => {
    let state: any = {
      authOrigin: authOriginUIDs.generalAuth,
      pathname:
        router.pathname === routes.giftCard
          ? giftCardConfigs.redirectGiftCardUrl
          : defaultRoute,
      query: { teamSchema },
    };
    if (platform === vendorUUIDs.ticketmaster) {
      handleConnectTm(state);
    }

    if (platform === vendorUUIDs.tickesdotcom) {
      if (shouldUseInternalLogin) {
        state = window.btoa(JSON.stringify(state));
        return await handleInternalRedirect(routes.auth, state, authUIDs.login);
      }

      await handleLoginOkta(state);
    }
  };

  const handleLogOut = async () => {
    const logoutRoute =
      router.pathname === routes.giftCard
        ? routes.giftCardNoQuery
        : routes.landingNoQuery;

    if (platform === vendorUUIDs.ticketmaster) {
      logoutTicketMaster();
    }

    if (!!oktaIssuer && !!oktaClientId) {
      if (shouldUseInternalLogin) return handleInternalLogOut();

      await handleSignOut(logoutRoute);
    }
  };

  return (
    <Box fontSize={"22px"}>
      <IconButton onClick={handleMenuClick}>
        <AiOutlineMenu />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClick={handleClose}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {userIsLegible && <HamburgerMenuUser user={user} />}

        {availableCreditEnabled &&
          userIsLegible &&
          !isGiftCardStandalonePage ? (
          <Box>
            <Stack paddingX={2} paddingY={1}>
              <Typography fontSize={"12px"}>
                {t("hamburgerMenu.availableCreditItemLabel") ||
                  "Available Credit"}
              </Typography>
              <Typography fontSize={"24px"}>
                {getUSCurrency(availableCredit)}
              </Typography>
            </Stack>
            <Box marginBottom="8px">
              <Divider />
            </Box>
          </Box>
        ) : null}

        {props?.renderDynamicItems?.()}

        {userIsLegible &&
          !isGiftCardStandalonePage &&
          purchaseHistoryEnabled && (
            <MenuItem
              sx={{ paddingX: 2 }}
              onClick={() => handleGoToRoute(routes.purchaseHistory)}
            >
              <ListItemIcon>
                <ShoppingCartCheckoutRoundedIcon />
              </ListItemIcon>
              {t("hamburgerMenu.purchaseHistoryItemLabel") ||
                "Purchase History"}
            </MenuItem>
          )}

        {/* menu items from team config */}
        {languages?.topNavMenuItems}

        {/* custom intercom launcher for mobile */}
        {mdDown && intercomConfigs?.isIntercomEnabled ? (
          <MenuItem sx={{ paddingRight: "24px" }} id="custom-intercom-launcher">
            <ListItemIcon>
              <MarkUnreadChatAltOutlinedIcon />
            </ListItemIcon>
            <ListItemText>
              {t("hamburgerMenu.chatWithSupportItemLabel") ||
                "Chat with support"}
            </ListItemText>
          </MenuItem>
        ) : null}

        {userIsLegible && manageCardOption ? (
          <MenuItem
            sx={{ paddingX: 2 }}
            onClick={() => handleGoToRoute(routes.manageCard)}
          >
            <ListItemIcon>
              <CreditCardRounded />
            </ListItemIcon>
            {t("hamburgerMenu.manageCardItemLabel") || "Manage Card"}
          </MenuItem>
        ) : null}

        {userIsLegible && logoutButtonEnabled ? (
          <MenuItem sx={{ paddingX: 2 }} onClick={handleLogOut}>
            <ListItemIcon>
              <LogoutRoundedIcon />
            </ListItemIcon>
            {t("hamburgerMenu.logoutItemLabel") || "Logout"}
          </MenuItem>
        ) : null}

        {!userIsLegible && !uiConfigs?.hamburgerMenu?.hideLoginButton ? (
          <MenuItem
            sx={{ paddingX: 2 }}
            onClick={() => handleLogin()}
            disabled={authConfigs?.isLoginDisabled}
          >
            <ListItemIcon>
              <LoginRoundedIcon />
            </ListItemIcon>
            {t("hamburgerMenu.loginItemLabel") ||
              optionsMenuLoginButtonLanguage}
          </MenuItem>
        ) : null}

        {uiConfigs?.hamburgerMenu?.disclaimer ? (
          <>
            {uiConfigs?.hamburgerMenu?.disclaimer}
          </>
        ) : null}
      </Menu>
    </Box>
  );
};

export default OptionsMenu;
